import { Switch, Route, Router as WouterRouter, useLocation } from "wouter";
import { Helmet } from "react-helmet-async";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { organizationSchema, websiteSchema } from "@/lib/seo";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/lib/auth";
import { AdminAuthProvider, useAdminAuth } from "@/lib/adminAuth";
import Home from "@/pages/Home";
import About from "@/pages/About";
import HowItWorksPage from "@/pages/HowItWorksPage";
import FAQsPage from "@/pages/FAQsPage";
import SignIn from "@/pages/SignIn";
import Admin from "@/pages/Admin";
import AdminLogin from "@/pages/AdminLogin";
import NotFound from "@/pages/not-found";
import { useEffect } from "react";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
});

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID ?? "";

function AdminGuard() {
  const { isAuthenticated } = useAdminAuth();
  const [, setLocation] = useLocation();
  useEffect(() => {
    if (!isAuthenticated) setLocation("/admin/login");
  }, [isAuthenticated]);
  if (!isAuthenticated) return null;
  return <Admin />;
}

function Router() {
  return (
    <Switch>
      <Route path="/" component={Home} />
      <Route path="/about" component={About} />
      <Route path="/how-it-works" component={HowItWorksPage} />
      <Route path="/faqs" component={FAQsPage} />
      <Route path="/signin" component={SignIn} />
      <Route path="/admin/login" component={AdminLogin} />
      <Route path="/admin" component={AdminGuard} />
      <Route component={NotFound} />
    </Switch>
  );
}

function AppInner({ ssrPath }: { ssrPath?: string }) {
  return (
    <AuthProvider>
      <AdminAuthProvider>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <Helmet>
              <script type="application/ld+json">
                {JSON.stringify(organizationSchema)}
              </script>
              <script type="application/ld+json">
                {JSON.stringify(websiteSchema)}
              </script>
            </Helmet>
            <WouterRouter
              base={import.meta.env.BASE_URL.replace(/\/$/, "")}
              ssrPath={ssrPath}
            >
              <Router />
            </WouterRouter>
            <Toaster />
          </TooltipProvider>
        </QueryClientProvider>
      </AdminAuthProvider>
    </AuthProvider>
  );
}

function App({ ssrPath }: { ssrPath?: string }) {
  if (GOOGLE_CLIENT_ID) {
    return (
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <AppInner ssrPath={ssrPath} />
      </GoogleOAuthProvider>
    );
  }
  return <AppInner ssrPath={ssrPath} />;
}

export default App;
